import * as React from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { useCurrentTenantId } from '@/components/tenant/tenant-context'
import { GuestsRequestError } from '@/lib/bff/guests'
import {
  downloadRecord,
  produceRecord,
  recordPage,
  type AccessPeriod,
  type AccessRecord,
  type AccessRecordFile,
} from '@/lib/bff/guest-access'

export const ACCESS_PAGE_SIZE = 25

type Step = { kind: 'produce' } | { kind: 'page'; index: number }

type RecordState = {
  period: AccessPeriod | null
  /** Pages already shown, by 0-based index; a production's pages never change. */
  pages: AccessRecord[]
  page: number
  status: 'idle' | 'loading' | 'data' | 'error'
  error: unknown
  failed: Step | null
}

const IDLE: RecordState = { period: null, pages: [], page: 0, status: 'idle', error: null, failed: null }

/** How a request ended; `stale` when a later request, a tenant switch or leaving the page overtook it. */
export type RecordOutcome = { status: 'data' } | { status: 'error'; error: unknown } | { status: 'stale' }

/** A download finished, or the tenant or period it started for is no longer on screen. */
export type DownloadOutcome = { status: 'file'; file: AccessRecordFile } | { status: 'stale' }

/*
 * Setup › Guest data access (VIS-330). Not SWR: every request is itself a
 * recorded production, so nothing may repeat on focus, reconnect or remount.
 * `show` produces the record for a period; `goToPage` moves through that
 * production with the server's `next` tokens, keeping pages already shown;
 * `retry` repeats what failed. Each resolves with how it ended. A page that
 * comes back as a new production replaces the pages already shown: a production
 * older than an hour, or another caller's, tenant's or unknown, is produced
 * again, and the expired snapshot must not stay on screen. A tenant switch
 * forgets the record. A download that outlives its tenant or period is dropped.
 */
export function useGuestAccessRecord() {
  const { getAccessTokenSilently } = useAuth0()
  const tenantId = useCurrentTenantId()
  const [state, setState] = React.useState<RecordState>(IDLE)
  const latest = React.useRef(0)
  /** Bumped when a download's tenant or period is no longer the one on screen, or the page is left. */
  const screen = React.useRef(0)
  const stateRef = React.useRef(state)
  React.useEffect(() => {
    stateRef.current = state
  })

  const [shownTenant, setShownTenant] = React.useState(tenantId)
  if (shownTenant !== tenantId) {
    setShownTenant(tenantId)
    setState(IDLE)
    stateRef.current = IDLE
    screen.current++
  }
  React.useLayoutEffect(
    () => () => {
      latest.current++
      screen.current++
    },
    [tenantId],
  )

  const token = React.useCallback(async () => {
    const value = await getAccessTokenSilently()
    if (!value) throw new GuestsRequestError(401, null, [], 'No access token available')
    return value
  }, [getAccessTokenSilently])

  const run = React.useCallback(
    async (step: Step, period: AccessPeriod, pages: AccessRecord[]): Promise<RecordOutcome> => {
      if (!tenantId) return { status: 'stale' }
      const call = ++latest.current
      if (step.kind === 'produce') screen.current++
      const page = step.kind === 'produce' ? 0 : step.index
      setState({ period, pages, page, status: 'loading', error: null, failed: null })
      try {
        const bearer = await token()
        const previous = pages[page - 1]
        const result =
          step.kind === 'produce' || !previous?.page.next
            ? await produceRecord(bearer, tenantId, period)
            : await recordPage(bearer, tenantId, previous.productionId, previous.page.next)
        if (call !== latest.current) return { status: 'stale' }
        // Produced again: this page is a new snapshot, so it cannot be appended to the expired one.
        const reissued = step.kind === 'page' && pages.some((cached) => cached.productionId !== result.productionId)
        const next = step.kind === 'produce' || reissued ? [result] : [...pages.slice(0, page), result]
        setState({ period, pages: next, page: reissued ? 0 : page, status: 'data', error: null, failed: null })
        return { status: 'data' }
      } catch (error) {
        if (call !== latest.current) return { status: 'stale' }
        setState({ period, pages, page, status: 'error', error, failed: step })
        return { status: 'error', error }
      }
    },
    [tenantId, token],
  )

  const show = React.useCallback((period: AccessPeriod) => run({ kind: 'produce' }, period, []), [run])

  const goToPage = React.useCallback(
    async (index: number): Promise<RecordOutcome> => {
      const current = stateRef.current
      if (!current.period || index < 0) return { status: 'stale' }
      if (current.pages[index]) {
        latest.current++
        setState({ ...current, page: index, status: 'data', error: null, failed: null })
        return { status: 'data' }
      }
      return run({ kind: 'page', index }, current.period, current.pages)
    },
    [run],
  )

  const retry = React.useCallback(async (): Promise<RecordOutcome> => {
    const current = stateRef.current
    if (!current.period || !current.failed) return { status: 'stale' }
    return run(current.failed, current.period, current.failed.kind === 'produce' ? [] : current.pages)
  }, [run])

  const download = React.useCallback(async (): Promise<DownloadOutcome> => {
    const current = stateRef.current
    if (!tenantId || !current.period) throw new GuestsRequestError(0, null, [], 'No period to download')
    const generation = screen.current
    const period = current.period
    try {
      const bearer = await token()
      if (generation !== screen.current) return { status: 'stale' }
      const file = await downloadRecord(bearer, tenantId, period)
      if (generation !== screen.current) return { status: 'stale' }
      return { status: 'file', file }
    } catch (error) {
      if (generation !== screen.current) return { status: 'stale' }
      throw error
    }
  }, [tenantId, token])

  const first = state.pages[0]
  const shown = state.status === 'data' ? state.pages[state.page] : undefined
  return {
    status: state.status,
    period: state.period,
    error: state.error,
    /** The page on screen, when there is one. */
    record: shown,
    page: state.page,
    total: first?.total ?? 0,
    totalPages: first ? Math.max(1, Math.ceil(first.total / ACCESS_PAGE_SIZE)) : 0,
    timeZone: first?.timeZone ?? null,
    show,
    goToPage,
    retry,
    download,
  }
}
